import { consume } from '@lit/context';
import { css, html, LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';

import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { selectorBadges, selectorIcons, selectorTooltip } from '../../../theme/selector.css';
import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';
import { MAX_UPGRADE_LEVEL, UPGRADE_FACTORS, UPGRADES } from '../loadout-data';

/**
 * Upgrade Selector - Displays upgrade selection buttons with level indicators
 *
 * Shows on the left side of the game screen (below aircraft selector) with:
 * - 4 upgrade buttons (Speed, Defense, Energy, Missile)
 * - Level counter on each (0-5)
 * - "lighted" state when upgrades are available and not maxed
 * - Detailed tooltips with level and bonus percentage
 *
 * Desktop only — on mobile, airmash-game omits it from the sidebar and the
 * toolbar loadout-panel popover takes over (clears the touch-control zones).
 *
 * @element upgrade-selector
 */
@customElement('upgrade-selector')
export class UpgradeSelector extends LitElement {
  static override styles = [
    tokens,
    selectorTooltip,
    selectorIcons,
    selectorBadges,
    css`
      :host {
        display: block;
        width: 44px;
        pointer-events: auto;
        /* Declared here rather than inherited from the body — the component
           owns its own typography, and a slow click on a button or its card
           shouldn't select the text. Same set as aircraft- and cruise-selector. */
        font-family: var(--font-family);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
      }

      .upgrade {
        position: relative;
        width: 44px;
        height: 44px;
        cursor: pointer;
        background-color: var(--color-bg-selector);
        background-repeat: no-repeat;
        background-size: 44px 44px;
        background-position: 0 0;
        /* Harmless on desktop; keeps taps crisp on a touch screen the mobile
           check misses — no double-tap-zoom hesitation or grey tap flash. */
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
      }

      .upgrade:first-child {
        border-top-left-radius: var(--radius-md);
        border-top-right-radius: var(--radius-md);
      }

      .upgrade:last-child {
        border-bottom-left-radius: var(--radius-md);
        border-bottom-right-radius: var(--radius-md);
      }

      .upgrade.lighted {
        background-color: var(--color-bg-selector-lighted);
        box-shadow: var(--shadow-lighted);
      }

      /* Hover-only — the sidebar is desktop-only, but a touch-only device the
         mobile check doesn't catch still renders it, and there a tap would
         leave the button lit (and its card open, below) after the finger lifts.
         Same gate as aircraft- and cruise-selector. */
      @media (hover: hover) {
        .upgrade:hover {
          background-color: var(--color-bg-selector-hover);
        }

        .upgrade.lighted:hover {
          background-color: var(--color-bg-selector-lighted-hover);
        }
      }

      /* The per-upgrade icon (keyed by upgrade.key) and the .level counter
         come from selectorIcons / selectorBadges, shared with loadout-panel. */

      /* Tooltip — the shared shell (container, arrow, header, .item, desc,
         clickto) lives in selectorTooltip. Below are the upgrade-specific level
         badge / percent / requires children and the hover trigger, gated like
         the hover above. */
      @media (hover: hover) {
        .upgrade:hover .tooltip {
          display: block;
        }
      }

      .tooltip .item .level-badge {
        display: inline-block;
        font-weight: bold;
        padding: 3px 8px;
        background-color: var(--color-bg-badge);
        font-size: var(--font-size-md);
        color: var(--color-text-white);
        border-radius: var(--radius-md);
      }

      .tooltip .item .percent {
        display: inline-block;
        font-size: var(--font-size-16);
        font-weight: bold;
        color: var(--color-text-white);
        margin-left: 10px;
      }

      .tooltip .item.smaller {
        margin-top: 2px;
      }

      .tooltip .item .requires {
        padding-top: 12px;
        color: var(--color-text-tooltip);
      }
    `,
  ];

  /** UI state from context */
  private uiState = bindValtio(this, uiStateProxy);

  /** Game API from context */
  @consume({ context: gameApiContext })
  private gameApi!: GameAPIType;

  /** The hover tooltip is informational (cursor: default) but sits inside the
   *  clickable .upgrade button, so a click on the card would otherwise bubble
   *  up and spend an upgrade point — and the card is reachable, since its arrow
   *  bridges the gap from the button and it lies over the game view, where a
   *  mouse-mode player clicks to fire. Swallow it. Click only — hover still
   *  bubbles, so moving onto the card to read it keeps it open. Same guard as
   *  aircraft-selector. */
  private stopTooltipClick = (e: Event) => {
    e.stopPropagation();
  };

  override render() {
    const { upgradeLevels, availableUpgrades } = this.uiState;

    return html`
      ${UPGRADES.map((upgrade) => {
        const maxLevel = MAX_UPGRADE_LEVEL;
        // Clamp defensively — level is server-sourced and should be 0..5, but a
        // stray value would otherwise index past the factor table and render a
        // nonsensical badge ("7 / 5").
        const level = Math.max(0, Math.min(maxLevel, upgradeLevels[upgrade.key] ?? 0));
        const isMaxed = level >= maxLevel;
        const isUpgradeable = availableUpgrades > 0 && !isMaxed;
        const factors = UPGRADE_FACTORS[upgrade.key];
        const currentFactor = factors?.[level] ?? 1;
        const bonusPercent = Math.round(100 * (currentFactor - 1));
        const nextFactor = factors?.[level + 1] ?? 1;
        const nextBonusPercent = Math.round(100 * (nextFactor - 1));

        return html`
          <div
            class="upgrade ${upgrade.key} ${isUpgradeable ? 'lighted' : ''}"
            @click=${() => this.handleClick(upgrade.index)}
          >
            <div class="level">${level}</div>
            <div class="tooltip" @click=${this.stopTooltipClick}>
              <div class="arrow"></div>
              <div class="header">${upgrade.name}</div>
              <div class="item">
                <span class="level-badge">${level} / ${maxLevel}</span>
                ${level > 0 ? html`<span class="percent">+${bonusPercent}%</span>` : nothing}
              </div>
              ${
                isMaxed
                  ? html`
                      <div class="item smaller">
                        <div class="clickto">Max upgrade reached</div>
                      </div>
                    `
                  : isUpgradeable
                    ? html`
                        <div class="item smaller">
                          <div class="requires">Requires 1 upgrade point</div>
                        </div>
                        <div class="item smaller">
                          <div class="clickto">Click to upgrade to +${nextBonusPercent}%</div>
                        </div>
                      `
                    : html`
                        <div class="item smaller">
                          <div class="requires">No upgrade points available</div>
                        </div>
                      `
              }
            </div>
          </div>
        `;
      })}
    `;
  }

  /** Handle upgrade button click */
  private handleClick(index: 1 | 2 | 3 | 4): void {
    this.gameApi.playClick();
    this.gameApi.selectUpgrade(index);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'upgrade-selector': UpgradeSelector;
  }
}
